import { useRef, useState } from 'react';

import { useOneOffSubscription } from 'helpers/action_cable/use_one_off_subscription';

import type { StreamFrame } from '../types';
import type { ToolCall } from '../tools/types';

export interface TurnResult {
  toolCalls?: ToolCall[];
  error?: boolean;
  interrupted?: boolean;
  noReply?: boolean;
}

// Return false when no frames are expected
type Start = (requestId: string) => Promise<boolean>;

const isTerminal = (frame: StreamFrame) => frame.type === 'done' || frame.type === 'error';

// Subscribe before the request that feeds it, so no frame can land before there is something listening,
// then stream until a terminal one arrives
export function useTurnSubscription() {
  const { subscribe, unsubscribe } = useOneOffSubscription();
  const [loading, setLoading] = useState(false);
  const activeRef = useRef<((result: TurnResult) => void) | null>(null);

  function run(start: Start, onFrame: (frame: StreamFrame) => void): Promise<TurnResult> {
    return new Promise<TurnResult>((resolve) => {
      let settled = false;
      setLoading(true);

      function finish(result: TurnResult) {
        if (settled) return;

        settled = true;
        activeRef.current = null;
        setLoading(false);
        resolve(result);
      }

      activeRef.current = finish;

      subscribe<StreamFrame>(
        {
          async connected(requestId: string) {
            try {
              if (await start(requestId) || settled) return;
            } catch (error) {
              if (settled) return;
              throw error;
            }

            unsubscribe();
            finish({ noReply: true });
          },

          connectedError() {
            if (settled) return;

            onFrame({ type: 'error', error: true });
            finish({ error: true });
          },

          received(frame) {
            if (settled) return;

            onFrame(frame);
            if (frame.type === 'done') {
              finish({ toolCalls: frame.toolCalls });
            } else if (frame.type === 'error') {
              finish({ error: true });
            }
          },
        },
        { isTerminal }
      );
    });
  }

  function stop(): boolean {
    const finish = activeRef.current;
    if (!finish) return false;

    unsubscribe();
    finish({ interrupted: true });
    return true;
  }

  return { loading, run, stop };
}
